import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import {
  RootState,
  selectActiveWorkspace,
  selectWorkspacesLoaded,
  setActiveWorkspace,
  WorkspaceSummary,
} from '@helios/state-management'
import { Store } from '@ngrx/store'
import Fuse from 'fuse.js'
import { Subject } from 'rxjs'
import {
  distinctUntilChanged,
  take,
  takeUntil,
  takeWhile,
  tap,
} from 'rxjs/operators'
import { storageUpdate$ } from '../../../services/ui-storage.service'
import { WorkspaceService } from '../../../services/workspace.service'
import {
  CreateNewWorkspaceModalAction,
  CreateNewWorkspaceModalService,
} from '../create-new-workspace-modal/create-new-workspace-modal.service'

@Component({
  selector: 'hs-workspace-catalog',
  templateUrl: './workspace-catalog.component.html',
  styleUrls: ['./workspace-catalog.component.scss'],
})
export class WorkspaceCatalogComponent implements OnInit, OnDestroy {
  @Output() closePanel = new EventEmitter<void>()
  @Input() isVisible = false

  _customWorkspaces?: WorkspaceSummary[]
  set customWorkspaces(summaries: WorkspaceSummary[]) {
    this._customWorkspaces = summaries
    this.setFilteredCustomWorkspacesFuse(summaries)
    this.filterCustomWorkspaces(this.workspaceSearch.value)
  }

  _presetWorkspaces?: WorkspaceSummary[]
  set presetWorkspaces(summaries: WorkspaceSummary[]) {
    this._presetWorkspaces = summaries
    this.setFilteredPresetWorkspacesFuse(summaries)
    this.filterPresetWorkspaces(this.workspaceSearch.value)
  }
  defaultWorkspacePrefix?: string

  filteredCustomWorkspaces?: WorkspaceSummary[]
  filteredCustomWorkspacesFuse?: Fuse<WorkspaceSummary>

  filteredPresetWorkspaces?: WorkspaceSummary[]
  filteredPresetWorkspacesFuse?: Fuse<WorkspaceSummary>

  readonly workspaceSearch = new FormControl('')

  private readonly destroySubject$ = new Subject<void>()

  constructor(
    private readonly store: Store<RootState>,
    private readonly service: WorkspaceService,
    private readonly createNewWorkspaceModalService: CreateNewWorkspaceModalService,
  ) {}

  async ngOnInit() {
    await this.store
      .select(selectWorkspacesLoaded)
      .pipe(
        takeUntil(this.destroySubject$),
        takeWhile(loaded => !loaded, true),
      )
      .toPromise()

    storageUpdate$
      .asObservable()
      .pipe(
        takeUntil(this.destroySubject$),
        tap((prefix: string) => {
          //TODO temporary solution until we get an SSE PVAL-12762
          if (!prefix.includes('workspace')) return
          if (prefix.endsWith('default')) this.updateDefaultPrefix()
          if (prefix.endsWith('summary')) {
            this.updateCustomWorkspace(prefix.slice(0, -8)) // remove /summary
          }
        }),
      )
      .subscribe()

    await this.updateCustomWorkspaces()
    await this.updateDefaultPrefix()

    const presetWorkspaceSummaries =
      await this.service.getPresetWorkspaceSummaries()
    this.presetWorkspaces = presetWorkspaceSummaries

    this.workspaceSearch.valueChanges
      .pipe(
        takeUntil(this.destroySubject$),
        distinctUntilChanged(),
        tap((search: string) => {
          this.filterPresetWorkspaces(search)
          this.filterCustomWorkspaces(search)
        }),
      )
      .subscribe()
  }

  ngOnDestroy() {
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  async updateCustomWorkspace(prefix: string): Promise<void> {
    if (this._customWorkspaces == null) return

    const updatedWorkspace = await this.service.getWorkspaceSummary(prefix)
    if (updatedWorkspace == null) {
      //workspace was deleted
      this.customWorkspaces = this._customWorkspaces.filter(
        w => w.workspacePrefix !== prefix,
      )
      return
    }

    if (!this._customWorkspaces.some(w => w.workspacePrefix === prefix)) {
      //workspace was created
      this.customWorkspaces = [...this._customWorkspaces, updatedWorkspace]
    }

    const updatedCustomWorkspaces: WorkspaceSummary[] = []
    for (const w of this._customWorkspaces) {
      if (prefix === w.workspacePrefix) {
        updatedCustomWorkspaces.push(updatedWorkspace)
        continue
      }
      updatedCustomWorkspaces.push(w)
    }

    this.customWorkspaces = updatedCustomWorkspaces
  }

  async updateCustomWorkspaces() {
    const userWorkspaceSummaries =
      await this.service.getUserWorkspaceSummaries()
    this.customWorkspaces = userWorkspaceSummaries
  }

  async updateDefaultPrefix() {
    const defaultPrefix = await this.service.getDefaultWorkspacePrefix()
    this.defaultWorkspacePrefix = defaultPrefix
  }

  async cloneActiveWorkspace(): Promise<void> {
    const activeWorkspace = await this.store
      .select(selectActiveWorkspace)
      .pipe(take(1))
      .toPromise()
    if (activeWorkspace == null) return
    await this.cloneWorkspace(activeWorkspace.workspacePrefix)
  }

  async cloneWorkspace(workspacePrefix: string): Promise<void> {
    const toBeClonedWorkspaceSummary = await this.service.getWorkspaceSummary(
      workspacePrefix,
    )
    if (toBeClonedWorkspaceSummary == null) return
    const response = await this.createNewWorkspaceModalService
      .toggle(
        toBeClonedWorkspaceSummary.name,
        toBeClonedWorkspaceSummary.description,
      )
      .catch(() => {
        //Do Nothing
      })
    if (response == null) return

    const newWorkspacePrefix = await this.service.cloneWorkspace(
      workspacePrefix,
      response.name,
      response.description,
    )

    if (newWorkspacePrefix == null) return

    if (response.action === CreateNewWorkspaceModalAction.SAVE_AND_ACTIVATE) {
      await this.activateWorkspace(newWorkspacePrefix)
    }
  }

  private async activateWorkspace(workspacePrefix: string): Promise<void> {
    const workspaceToActivate = await this.service.getWorkspace(workspacePrefix)

    if (workspaceToActivate == null) return
    this.store.dispatch(setActiveWorkspace(workspaceToActivate))
  }

  private filterCustomWorkspaces(search: string): void {
    if (search === '' || this.filteredCustomWorkspacesFuse == null) {
      this.filteredCustomWorkspaces = this._customWorkspaces
    } else {
      this.filteredCustomWorkspaces = this.applyFilter(
        search,
        this.filteredCustomWorkspacesFuse,
      )
    }
  }

  private filterPresetWorkspaces(search: string): void {
    if (search === '' || this.filteredPresetWorkspacesFuse == null) {
      this.filteredPresetWorkspaces = this._presetWorkspaces
    } else {
      this.filteredPresetWorkspaces = this.applyFilter(
        search,
        this.filteredPresetWorkspacesFuse,
      )
    }
  }

  private applyFilter(
    search: string,
    fuse: Fuse<WorkspaceSummary>,
  ): WorkspaceSummary[] {
    return fuse.search(search).map(result => {
      return result.item
    })
  }

  private setFilteredCustomWorkspacesFuse(
    summariesToFilter: WorkspaceSummary[],
  ) {
    if (this.filteredCustomWorkspacesFuse == null) {
      this.filteredCustomWorkspacesFuse =
        this.createSummariesFuse(summariesToFilter)
    } else {
      this.filteredCustomWorkspacesFuse.setCollection(summariesToFilter)
    }
  }

  private setFilteredPresetWorkspacesFuse(
    summariesToFilter: WorkspaceSummary[],
  ) {
    if (this.filteredPresetWorkspacesFuse == null) {
      this.filteredPresetWorkspacesFuse =
        this.createSummariesFuse(summariesToFilter)
    } else {
      this.filteredPresetWorkspacesFuse.setCollection(summariesToFilter)
    }
  }

  private createSummariesFuse(
    summariesToFilter: WorkspaceSummary[],
  ): Fuse<WorkspaceSummary> {
    return new Fuse(summariesToFilter, {
      keys: ['name', 'description'],
      threshold: 0.4,
      fieldNormWeight: 0,
      isCaseSensitive: false,
    })
  }
}
